<app-header class="full-width"></app-header>
<main id="content" class="js-content" role="main">
  <span name="top" id="top"></span>
  <app-breadcrumbs [breadcrumbs]="[{ url: '.', name: 'Home' }, { name: 'Accessibility Statement' } ]"></app-breadcrumbs>
  <div class="grid-row">
    <div class="column-two-thirds">
      <h1 class="heading-xlarge">Accessibility statement for the multiplication tables check service</h1>
      <p>
        This statement applies to content published on the multiplication tables check (MTC) service.
        This includes the school administration application accessed via <a class="govuk-link" target="_blank" rel="noopener noreferrer"
        href="https://services.signin.education.gov.uk/">DfE Sign-in</a> and the pupil try it out and official
        <a class="govuk-link" target="_blank" rel="noopener noreferrer"
        href="https://pupil.multiplication-tables-check.service.gov.uk/sign-in">
        check application</a>
      </p>

      <p>
        We are committed to making the MTC service accessible and user-friendly for school staff and pupils, regardless of any physical, cognitive or emotional impairment.
      </p>

      <p>
        You can read a <a target="_blank" rel="noopener noreferrer" href="https://accessibility.blog.gov.uk/">
        blog about accessibility</a> <img alt="" src="/images/icon-link.png"/> on GOV.UK.
      </p>

      <h3 class="heading-medium">Our commitment to accessibility</h3>

      <p>
        The Standards and Testing Agency (STA) is committed to making websites accessible,
        in accordance with <a href="https://www.legislation.gov.uk/uksi/2018/952/contents/made"
        class="govuk-link"  rel="noopener noreferrer"  target="_blank"
        >The Public Sector Bodies (Websites and Mobile Applications) (No.2) Accessibility Regulations 2018</a>.
      </p>

      <h3 class="heading-medium">Conformance status</h3>

      <p>
        We seek to ensure that most of the service meets the
        <a target="_blank" rel="noopener noreferrer" href="https://www.w3.org/WAI/standards-guidelines/wcag/">
          Web Content Accessibility Guidelines (WCAG)</a> <img alt="" src="/images/icon-link.png"/>
        2.2 at Level AA. The MTC is partially conformant with WCAG 2.2 level AA.
      </p>

      <p>Partially conformant means that some parts of the content do not fully conform to the accessibility standard.</p>

      <h3 class="heading-medium">Pupil site</h3>

      <p>The MTC pupil application does not support third-party screen readers due to compatibility issues with the timed element of the check.</p>

      <p>
        To address this and other accessibility needs, a number of access arrangements can be applied to the pupil check.
        The following table shows which access arrangement compatibility with browsers and devices.
      </p>

      <table class="table-font-xsmall">
        <thead>
        <tr>
          <th>Access arrangement</th>
          <th>Browser and device compatibility</th>
        </tr>
        </thead>
        <tbody>
        <tr>
          <td>Audible time alert</td>
          <td>Not compatible with any version of Internet Explorer and below version 14 for Microsoft Edge</td>
        </tr>
        <tr>
          <td>Colour contrast</td>
          <td>Any browser/device</td>
        </tr>
        <tr>
          <td>Font size</td>
          <td>Any browser/device</td>
        </tr>
        <tr>
          <td>Input assistance</td>
          <td>Any browser/device</td>
        </tr>
        <tr>
          <td>Pause ‘next’ button between questions</td>
          <td>Any browser/device</td>
        </tr>
        <tr>
          <td>Audio version</td>
          <td>Not recommended for iOS and Safari below version 16,
            not compatible with any version of Internet Explorer and below version 14 of Microsoft Edge
          </td>
        </tr>
        <tr>
          <td>Remove on-screen number pad</td>
          <td>Any browser/device (external keyboard required for tablets)</td>
        </tr>
        </tbody>
      </table>

      <h3 class="heading-medium">School administration website</h3>

      <p>The school administration website does not currently have any built-in access arrangements.</p>

      <p>The school administration website has been designed to be compatible with current versions (as of April 2026) of the following screen readers:</p>

      <ul class="list list-bullet">
        <li>job access with speech (JAWS)</li>
        <li>non-visual desktop access (NVDA)</li>
        <li>VoiceOver</li>
      </ul>


      <p>The website has also been designed to be compatible with:</p>

      <ul class="list list-bullet">
        <li>basic operating system screen magnifiers, such as ZoomText magnifier</li>
        <li>speech recognition software, such as Dragon Naturally Speaking</li>
        <li>operating system speech packages</li>
      </ul>

      <h3 class="heading-medium">Get help for the school administration website</h3>

      <p>AbilityNet provides guidance about how to:</p>

      <ul class="list list-bullet">
        <li><a target="_blank" rel="noopener noreferrer" href="https://mcmw.abilitynet.org.uk/impairment/making-your-mouse-easier-use">
          make your mouse easier to use</a> <img alt="" src="/images/icon-link.png"/></li>
        <li><a target="_blank" rel="noopener noreferrer" href="https://mcmw.abilitynet.org.uk/category/keyboard-shortcuts">
          use your keyboard instead of a mouse</a> <img alt="" src="/images/icon-link.png"/></li>
        <li><a target="_blank" rel="noopener noreferrer" href="https://mcmw.abilitynet.org.uk/impairment/talking-your-device">
          talk to your device</a> <img alt="" src="/images/icon-link.png"/></li>
        <li><a target="_blank" rel="noopener noreferrer" href="https://mcmw.abilitynet.org.uk/impairment/making-your-device-talk-you">
          make your device talk to you</a> <img alt="" src="/images/icon-link.png"/></li>
        <li><a target="_blank" rel="noopener noreferrer" href="https://mcmw.abilitynet.org.uk/impairment/making-text-larger">
          make text larger</a> <img alt="" src="/images/icon-link.png"/></li>
        <li><a target="_blank" rel="noopener noreferrer" href="https://mcmw.abilitynet.org.uk/impairment/changing-your-colours">
          change your colours</a> <img alt="" src="/images/icon-link.png"/></li>
        <li><a target="_blank" rel="noopener noreferrer" href="https://mcmw.abilitynet.org.uk/impairment/magnifying-screen">
          magnify the screen</a> <img alt="" src="/images/icon-link.png"/></li>
      </ul>

      <h3 class="heading-medium">How accessible this website is</h3>

      <p>Parts of the MTC service are not fully accessible for example:</p>
      <ul class="list list-bullet">
        <li>within the pupil check application, the interface is not announced to third-party screen reader users, including the on-screen number pad/calculator controls. This means third-party screen reader users cannot use this feature, regardless of the timed nature of the check. It should however be noted that the pupil check does have a built-in screen reader that can be enabled.</li>
        <li>within the pupil register, the pupil list table is not usable with a screen reader and per-row actions are not descriptive (for example, multiple links labelled only “View”). This makes the page unusable for screen reader users.</li>
        <li>on some Official Check pages, mobile zoom and text resizing are prevented due to viewport settings, which can be a barrier for low vision users.</li>
        <li>some pages include non-interactive elements in the tab order and inconsistent focus behaviour, increasing effort and confusion for keyboard users.</li>
        <li>some links and controls do not provide clear purpose (for example, empty links, missing href values, or generic link text).</li>
        <li>some feedback messages are not clearly associated with form fields, instead all feedback is presented at the top of the page.</li>
        <li>focus indicators and tab order for keyboard users may be inconsistent with the layout.</li>
        <li>some elements and images are missing descriptive alternative text, their function should, however, be evident by their location.</li>
        <li>the “loading wheel” animation continues for longer than 3 seconds and cannot be paused.</li>
        <li>users are not warned of the idle 5-minute time-out function on pages until notified by a notification box. There is no option to extend or turn off this 5-minute time-out function.</li>
        <li>the "skip to main content" function does not always move the cursor to the main content.</li>
        <li>the search function and filter controls automatically update the content presented as input is given.</li>
        <li>some tables in the service are not presented as tables or are missing headers. The layout is, however, consistent.</li>
        <li>the structure, formatting, and use of headings may not be consistent or defined across the service.</li>
        <li>with some presented information some elements do not have unique IDs for assistive technologies. Their content is, however, sufficiently unique to ascertain their purpose.</li>
        <li>some elements such as filters and controls will not be accessible by keyboard. Please use the search box where possible to navigate the site and manual operation may be required.</li>
        <li>screen reader users may have a different experience to content provided on screen.</li>
      </ul>

      <p class="govuk-body">These parts of the MTC service will be reviewed as part of our annual review of the service.</p>

      <h3 class="govuk-heading-m">Content not within the scope of the accessibility regulations</h3>

      <p class="govuk-body">We do not currently have any content that is out of scope of the accessibility regulations.</p>      

      <h3 class="heading-medium">How to request content in an accessible format</h3>

      <p>All content on MTC is in an accessible format.</p>

      <h3 class="heading-medium">Reporting accessibility problems with this website</h3>

      <p>If you find any problems that are not listed on this page
        or you think we are not meeting the accessibility requirements,
        please <a class="govuk-link"
        href="https://help.multiplicationtablescheckservice.education.gov.uk/hc/en-gb/requests/new?ticket_form_id=">contact us,</a> or alternatively contact the national curriculum assessments
        helpline on 0300 303 3013 (selecting the option for MTC).
      </p>

      <h3 class="heading-medium">Enforcement procedure</h3>

      <p>If you contact us with a complaint and you are not happy with our response,
        please <a href="https://www.equalityadvisoryservice.com/">contact the Equality Advisory and Support Service (EASS).</a>
      </p>

      <p>The Equality and Human Rights Commission
        (EHRC) is responsible for enforcing the <a
        class="govuk-link" target="_blank" rel="noopener noreferrer"
        href="https://www.legislation.gov.uk/uksi/2018/952/contents/made">
        Public Sector Bodies (Websites and Mobile Applications) (No. 2)
        Accessibility Regulations 2018 (the ‘accessibility
        regulations’)</a>.
      </p>

      <h3 class="heading-medium">How we tested this website</h3>

      <p>We have tested this website for compliance
        with the <a href="https://www.w3.org/TR/WCAG21/" class="govuk-link"
      target="_blank" rel="noopener noreferrer">
      Web Content Accessibility Guidelines V2.2</a> level A and level
      AA.
      </p>

      <h3 class="heading-medium">What we’re doing to improve accessibility</h3>

      <p class="govuk-body">We audit our content annually and update the service according to report findings.</p>

      <h3 class="govuk-heading-m">Preparation of this accessibility statement</h3>

      <p>This statement was prepared based on an
        independent accessibility audit of the full MTC service carried out
        in July 2019. The service is reviewed and this statement updated yearly. It was last reviewed in March 2026 following a limited, high-level sample accessibility review.</p>
      
        <div class="back-to-top-container">
        <a [routerLink]="['/accessibility-statement']" fragment="top">Back to top</a>
      </div>
    </div>
    <div class="column-one-third add-title-margin">
      <br />
      <aside role="complementary" class="support-column">
        <h2 class="heading-medium">Related</h2>
        <nav role="navigation" aria-labelledby="subsection-title">
          <ul class="govuk-list govuk-!-font-size-16">
            <li>
              <a class="govuk-link" href="https://www.gov.uk/government/organisations/department-for-education/about/personal-information-charter">Privacy notice on multiplication tables check</a>
            </li>
          </ul>
        </nav>
      </aside>
    </div>
  </div>
</main>
<app-footer></app-footer>
